import { AndroidAppPrompt } from '@/components/AndroidAppPrompt';
import { GlobalSyncManager } from '@/components/GlobalSyncManager';

import '@/config/amplify';
import { DarkTheme, DefaultTheme, ThemeProvider } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import { useFonts } from 'expo-font';
import { Slot } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { Platform } from 'react-native';

import env from '@/config/env';
import { useAndroidAppPrompt } from '@/hooks/useAndroidAppPrompt';
import { AuthProvider, useAuth } from '@/hooks/useAutoLogin';
import { useColorScheme } from '@/hooks/useColorScheme';
import { locationService } from '@/utils/locationService';
import { setupWakeLockErrorHandler } from '@/utils/wakeLockErrorHandler';

function AppContent() {
  const colorScheme = useColorScheme();
  const shouldShowAndroidPrompt = useAndroidAppPrompt();
  const [isAndroidPromptVisible, setIsAndroidPromptVisible] = useState(true);
  const { isAuthenticated } = useAuth();

  // Initialize WakeLock error handler for web platforms
  useEffect(() => {
    if (Platform.OS === 'web') {
      setupWakeLockErrorHandler();
    }
  }, []);

  // Request location permission only after user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Request location permission after login (all platforms including web)
      locationService.requestPermission().catch((error) => {
        // Silently handle permission request errors - location is optional
        console.warn('Location permission request failed:', error);
      });
    }
  }, [isAuthenticated]);

  return (
    <ThemeProvider value={colorScheme === 'dark' ? DarkTheme : DefaultTheme}>
      <GlobalSyncManager />
      <Slot />
      {/* Temporarily disabled: Android app download link is not working */}
      {false && shouldShowAndroidPrompt && (
        <AndroidAppPrompt
          visible={isAndroidPromptVisible}
          onDismiss={() => setIsAndroidPromptVisible(false)}
        />
      )}
    </ThemeProvider>
  );
}

export default Sentry.wrap(function RootLayout() {

  if (__DEV__) {
    console.log("Sentry is disabled in development mode");
  } else {
    Sentry.init({
      dsn: 'https://6bb1a914af9fdeace07318b00092e448@o4509685981118464.ingest.de.sentry.io/4509685985902672',
      environment: env.environment || '',

      // Adds more context data to events (IP address, cookies, user, etc.)
      // For more information, visit: https://docs.sentry.io/platforms/react-native/data-management/data-collected/
      sendDefaultPii: true,

      // Configure Session Replay
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1,
      integrations: [Sentry.mobileReplayIntegration(), Sentry.feedbackIntegration()],

      // uncomment the line below to enable Spotlight (https://spotlightjs.com)
      // spotlight: __DEV__,
    });
  }


  const [loaded] = useFonts({
    SpaceMono: require('@/assets/fonts/SpaceMono-Regular.ttf'),
  });

  if (!loaded) {
    return null;
  }

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
});